import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useConfig } from '../hooks/useConfig';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export function SettingsProvider({ children }) {
  const { personalities, categories, config } = useConfig();
  const { isAuthenticated, user, loading } = useAuth();
  
  // Get personalities for a specific category
  const getCategoryPersonalities = useCallback((categoryName) => {
    if (!categories || !categories[categoryName]) return [];
    return categories[categoryName].personalities || [];
  }, [categories]);
  
  // Get the first available category as default
  const getDefaultCategory = useCallback(() => {
    if (!categories) return 'default';
    const categoryNames = Object.keys(categories);
    return categoryNames.length > 0 ? categoryNames[0] : 'default';
  }, [categories]);
  
  // Get the first personality as default for a category
  const getDefaultPersonality = useCallback((categoryName = null) => {
    if (categoryName) {
      const categoryPersonalities = getCategoryPersonalities(categoryName);
      return categoryPersonalities.length > 0 ? categoryPersonalities[0].name : '';
    }
    // Fallback to backward compatibility with flat personalities array
    return personalities && personalities.length > 0 ? personalities[0].name : '';
  }, [personalities, getCategoryPersonalities]);
  
  // Check if multiple categories exist
  const hasMultipleCategories = () => {
    return categories && Object.keys(categories).length > 1;
  };
  
  // Load user settings from backend
  const loadUserSettings = useCallback(async () => {
    if (!isAuthenticated || !user) {
      console.log('[SETTINGS] User not authenticated, skipping settings load');
      return null;
    }
    
    try {
      console.log('[SETTINGS] Loading user settings from backend');
      const response = await fetchWithAuth('/api/user').catch(err => {
        console.warn('[SETTINGS] /api/user request threw:', err?.message || err);
        return null;
      });
      
      if (!response || typeof response.ok !== 'boolean') {
        console.warn('[SETTINGS] Invalid /api/user response, skipping settings load');
        return null;
      }
      
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const userSettings = data.user?.settings;
        
        if (userSettings) {
          console.log('[SETTINGS] Loaded user settings:', userSettings);
          const defaultCategory = getDefaultCategory();
          const savedCategory = userSettings.selectedCategory || defaultCategory;
          const defaultPersonality = getDefaultPersonality(savedCategory);
          
          // Read response length from user settings only
          const respLen = (userSettings.responseLength ?? 3);
         
         return {
            model: userSettings.selectedModel || config?.defaultModel || 'gpt-3.5-turbo',
            personality: userSettings.selectedPersonality || defaultPersonality,
            tone: userSettings.toneLevel || 3,
            category: savedCategory,
            responseLength: typeof respLen === 'number' && respLen >= 1 && respLen <= 5 ? respLen : 3
          };
        }
      } else {
        console.warn('[SETTINGS] Failed to load user settings:', response.status);
      }
    } catch (error) {
      console.error('[SETTINGS] Error loading user settings:', error);
    }
    
    return null;
  }, [config, isAuthenticated, user, getDefaultCategory, getDefaultPersonality]);
  
  // Save user settings to backend
  const saveUserSettings = useCallback(async (settingsToSave) => {
    if (!isAuthenticated || !user) {
      console.log('[SETTINGS] User not authenticated, skipping settings save');
      return false;
    }
    
    try {
      console.log('[SETTINGS] Saving user settings to backend:', settingsToSave);
      
      const payload = {
        selectedModel: settingsToSave.model,
        selectedPersonality: settingsToSave.personality,
        selectedCategory: settingsToSave.category,
        toneLevel: settingsToSave.tone,
        // Persist response length using camelCase, like other settings
        responseLength: settingsToSave.responseLength
      };
      
      const response = await fetchWithAuth('/api/user/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        console.error('[SETTINGS] Request error saving user settings:', err?.message || err);
        return null;
      });
      
      if (!response || typeof response.ok !== 'boolean') {
        console.error('[SETTINGS] Invalid /api/user/settings response while saving');
        return false;
      }
      
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        console.log('[SETTINGS] Settings saved successfully (PUT):', data.message || '(no message)');
        return true;
      } else {
        console.error('[SETTINGS] Failed to save settings:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[SETTINGS] Error saving user settings:', error);
      return false;
    }
  }, [isAuthenticated, user]);
  
  // Current settings state
  const [settings, setSettings] = useState(() => {
    const defaultCategory = getDefaultCategory();
    return {
      model: config?.defaultModel || 'gpt-3.5-turbo',
      category: defaultCategory,
      personality: getDefaultPersonality(defaultCategory),
      tone: 3, // Default tone level (Fair)
      responseLength: 3, // Default response length (Medium)
    };
  });
  
  // Update category and personality defaults when categories/personalities load
  useEffect(() => {
    if (categories && Object.keys(categories).length > 0) {
      const defaultCategory = getDefaultCategory();
      const defaultPersonality = getDefaultPersonality(defaultCategory);
      
      setSettings(prev => {
        const needsUpdate = !prev.category || !prev.personality;
        if (needsUpdate) {
          return {
            ...prev,
            category: prev.category || defaultCategory,
            personality: prev.personality || defaultPersonality
          };
        }
        return prev;
      });
    }
  }, [categories, personalities, getDefaultCategory, getDefaultPersonality]);
  
  // Load user settings when user authenticates
  useEffect(() => {
    const loadSettings = async () => {
      // Don't do anything while authentication is still loading
      if (loading) {
        console.log('[SETTINGS] Authentication still loading, waiting...');
        return;
      }
      
      if (isAuthenticated && user) {
        console.log('[SETTINGS] User authenticated, loading settings');
        const userSettings = await loadUserSettings();
        
        if (userSettings) {
          console.log('[SETTINGS] Applying loaded settings:', userSettings);
          setSettings(userSettings);
        } else {
          console.log('[SETTINGS] No saved settings found, using defaults');
          const defaultCategory = getDefaultCategory();
          setSettings({
            model: config?.defaultModel || 'gpt-3.5-turbo',
            category: defaultCategory,
            personality: getDefaultPersonality(defaultCategory),
            tone: 3,
            responseLength: 3
          });
        }
      } else {
        console.log('[SETTINGS] User not authenticated, resetting to defaults');
        // Reset to defaults when user logs out
        const defaultCategory = getDefaultCategory();
        setSettings({
          model: config?.defaultModel || 'gpt-3.5-turbo',
          category: defaultCategory,
          personality: getDefaultPersonality(defaultCategory),
          tone: 3,
          responseLength: 3
        });
      }
    };
    
    loadSettings();
  }, [config, loading, isAuthenticated, user, loadUserSettings, getDefaultCategory, getDefaultPersonality]);

  // Update individual setting with special handling for category changes
  const updateSetting = useCallback(async (key, value) => {
    let updatedSettings;
    
    if (key === 'category') {
      // When category changes, reset personality to first in new category
      const newPersonality = getDefaultPersonality(value);
      updatedSettings = {
        category: value,
        personality: newPersonality
      };
    } else {
      updatedSettings = { [key]: value };
    }
    
    // Update local state first
    setSettings(prev => {
      const newSettings = {
        ...prev,
        ...updatedSettings
      };
      
      // Save to backend after state update (non-blocking)
      if (isAuthenticated && user) {
        console.log('[SETTINGS] Setting changed, saving to backend:', { key, value });
        saveUserSettings(newSettings).catch(error => {
          console.error('[SETTINGS] Failed to save settings change:', error);
        });
      }
      
      return newSettings;
    });
  }, [getDefaultPersonality, isAuthenticated, user, saveUserSettings]);

  // Update multiple settings at once
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => {
      const updatedSettings = {
        ...prev,
        ...newSettings
      };
      
      // Save to backend after state update (non-blocking)
      if (isAuthenticated && user) {
        console.log('[SETTINGS] Multiple settings changed, saving to backend:', newSettings);
        saveUserSettings(updatedSettings).catch(error => {
          console.error('[SETTINGS] Failed to save settings changes:', error);
        });
      }
      
      return updatedSettings;
    });
  }, [isAuthenticated, user, saveUserSettings]);

  // Reset settings to defaults
  const resetSettings = useCallback(() => {
    const defaultCategory = getDefaultCategory();
    const defaultPersonality = getDefaultPersonality(defaultCategory);
    setSettings({
      model: config?.defaultModel || 'gpt-3.5-turbo',
      category: defaultCategory,
      personality: defaultPersonality,
      tone: 3, // Default tone level (Fair)
      responseLength: 3, // Default response length (Medium)
    });
  }, [config, getDefaultCategory, getDefaultPersonality]);

  const value = {
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    // Convenience getters
    selectedModel: settings.model,
    selectedCategory: settings.category,
    selectedPersonality: settings.personality,
    selectedTone: settings.tone,
    selectedResponseLength: settings.responseLength,
    // Helper methods
    getCategoryPersonalities,
    hasMultipleCategories,
    getDefaultPersonality,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
